//image-gallery

import { CarImage, NavigationParams } from "@interfaces/gallery";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { EXCLUSIVE_IMAGES_PER_PAGE } from "@configs/gallery";

export const filterImagesByCategory = (
  images: CarImage[],
  activeTab: string,
  activeSubTab: string | null
) => {
  let filteredImages = images.filter((img) => img.category === activeTab);
  if (activeSubTab) {
    filteredImages = filteredImages.filter((img) => img.type === activeSubTab);
  }
  return filteredImages;
};

export const getDisplayedImages = (
  filteredImages: CarImage[],
  page: number,
  imagesPerPage: number
) => {
  return filteredImages.slice(0, page * imagesPerPage);
};

export const hasMoreMainImages = (
  displayedImages: CarImage[],
  filteredImages: CarImage[]
) => {
  return displayedImages.length < filteredImages.length;
};

export const getImageCount = (
  images: CarImage[],
  activeSubTab: string | null,
  activeTab: string | null
) => {
  if (!activeSubTab) return 0;
  return images.filter(
    (img) => img.type === activeSubTab && img.category === activeTab
  ).length;
};

// varient-view
export const filterImages = (
  images: CarImage[],
  activeTab: string,
  activeSubTab?: string | null
) => {
  return images.filter((img) => {
    return (
      img.category === activeTab && (!activeSubTab || img.type === activeSubTab)
    );
  });
};

export const getSelectedAndRemainingImages = (
  filteredImages: CarImage[],
  variantId: string
) => {
  const selectedImage = filteredImages.find(
    (img) => img.variant_Id === variantId
  );
  const remainingImages = filteredImages.filter(
    (img) => img.variant_Id !== variantId
  );
  return { selectedImage, remainingImages };
};

export const getCurrentIndex = (
  filteredImages: CarImage[],
  variantId: string
) => {
  return filteredImages.findIndex((img) => img.variant_Id === variantId);
};

export const handlePrevious = (
  currentIndex: number,
  filteredImages: CarImage[],
  params: NavigationParams,
  activeTab: string,
  activeSubTab: string | null,
  router: AppRouterInstance
) => {
  const newIndex =
    (currentIndex - 1 + filteredImages.length) % filteredImages.length;
  const newImage = filteredImages[newIndex];
  router.push(
    `/cars/${params.slug}/images/${params.modelName}/${
      newImage.variant_Id
    }?activeTab=${activeTab}${
      activeSubTab ? `&activeSubTab=${activeSubTab}` : ""
    }`
  );
};

export const handleNext = (
  currentIndex: number,
  filteredImages: CarImage[],
  params: NavigationParams,
  activeTab: string,
  activeSubTab: string | null,
  router: AppRouterInstance
) => {
  const newIndex = (currentIndex + 1) % filteredImages.length;
  const newImage = filteredImages[newIndex];
  router.push(
    `/cars/${params.slug}/images/${params.modelName}/${
      newImage.variant_Id
    }?activeTab=${activeTab}${
      activeSubTab ? `&activeSubTab=${activeSubTab}` : ""
    }`
  );
};

//exclusive
export const getPaginatedImages = (images: CarImage[], currentPage: number) => {
  const startIndex = 0;
  const endIndex = currentPage * EXCLUSIVE_IMAGES_PER_PAGE;
  return images.slice(startIndex, endIndex);
};

export const hasMoreExclusiveImages = (
  images: CarImage[],
  currentPage: number
) => {
  return images.length > currentPage * EXCLUSIVE_IMAGES_PER_PAGE;
};

// Dynamic sub-tabs generation
export const getAvailableSubTabs = (images: CarImage[], activeTab: string) => {
  const availableTypes = [
    ...new Set(
      images.filter((img) => img.category === activeTab).map((img) => img.type)
    ),
  ];

  // Map types to display names
  const typeDisplayNames: Record<string, string> = {
    sunroof: "Sunroof",
    dashboard: "Dashboard",
    powersteering: "Power Steering",
    airbags: "Airbags",
    automaticmusic: "Automatic Music",
    amtwipr: "Automatic Wiper",
    headlight: "Headlight",
    taillamp: "Taillamp",
    topview: "Top view",
  };

  return availableTypes.map((type) => ({
    id: type,
    label:
      typeDisplayNames[type] || type.charAt(0).toUpperCase() + type.slice(1),
  }));
};

// Dynamic navigation tabs generation
export const getAvailableNavigationTabs = (images: CarImage[]) => {
  const availableCategories = [...new Set(images.map((img) => img.category))];

  // Map categories to display names
  const categoryDisplayNames: Record<string, string> = {
    exterior: "Exterior",
    interior: "Interior",
    safety: "Safety",
    tech: "Tech",
    videos: "Videos",
    "360": "360° View",
  };

  return availableCategories.map((category) => ({
    id: category,
    label:
      categoryDisplayNames[category] ||
      category.charAt(0).toUpperCase() + category.slice(1),
  }));
};
